import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import { DICT } from "@/i18n";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=guest, object=auth
  const [lang, setLang] = useState(localStorage.getItem("cu_lang") || "en");

  const t = useCallback((key) => (DICT[lang] && DICT[lang][key]) || DICT.en[key] || key, [lang]);

  const toggleLang = () => {
    const next = lang === "en" ? "it" : "en";
    setLang(next);
    localStorage.setItem("cu_lang", next);
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      // Access token may have expired — try the refresh token once before giving up.
      try {
        const { data: r } = await api.post("/auth/refresh");
        if (r?.token) localStorage.setItem("cu_token", r.token);
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        return data.user;
      } catch {
        setUser(null);
        return null;
      }
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("cu_token")) refresh();
    else setUser(null);
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("cu_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("cu_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("cu_token");
    setUser(null);
  };

  return (
    <AppContext.Provider value={{ user, setUser, lang, t, toggleLang, login, register, logout, refresh, formatError }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
